import { Button } from "@/components/ui/baseUi/button"
import { ErrorView } from "@/components/ui/layout/error/errorView"
import { Skeleton } from "@/components/ui/baseUi/skeleton"
import { errorToastClassName, successToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import { BoxProgrammationImportProgramSelection } from "@modules/boxB2BProgram/components/boxProgrammationImportProgramSelection"
import { useB2BProgramActions } from "@modules/boxB2BProgram/hooks/useB2BProgramActions"
import { word } from "@modules/core/utils/i18n"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import dayjs from "dayjs"
import { LoaderCircle } from "lucide-react"
import { Suspense, useState } from "react"
import { ErrorBoundary } from "react-error-boundary"

type BoxProgrammationImportProgramLayoutProps = {
  boxProgramId: string
  startDate: Date
  endDate: Date
  className?: string
} & React.ComponentProps<"div">

export function BoxProgrammationImportProgramLayout({
  boxProgramId,
  startDate,
  endDate,
  className,
  ...props
}: BoxProgrammationImportProgramLayoutProps) {
  const [selectProgramId, setSelectProgramId] = useState<string | null>(null)
  const { importProgramMutation, isPending } = useB2BProgramActions()
  const { hideSidebar } = useSidebar()

  const handleImportProgram = () => {
    if (!selectProgramId) return
    void importProgramMutation({
      boxProgramId: boxProgramId,
      programId: selectProgramId,
      startDate: startDate,
      endDate: endDate,
    })
      .then(() => {
        toast.success(word("box.programmation.activity.importTemplateForm.success"), {
          classNames: successToastClassName(),
        })
        hideSidebar()
      })
      .catch((error: Error) => {
        toast.error(word("box.programmation.activity.importTemplateForm.error.title"), {
          description:
            error.message === "No slots to apply sessions"
              ? word("box.programmation.activity.importTemplateForm.error.noSlot")
              : error.message === "No session to copy in the date range"
                ? word("box.programmation.activity.importTemplateForm.error.noSession")
                : error.message,
          classNames: errorToastClassName(),
        })
      })
  }

  return (
    <div className={cn("flex flex-col justify-between gap-6 px-4 pt-18", className)} {...props}>
      <div className="flex flex-col gap-2">
        <Text fontStyle="TL">{word("box.programmation.activity.importTemplateForm.title")}</Text>
        <Text fontStyle="TL">
          {word("global.dateInterval", {
            dateFrom: dayjs(startDate).format("LL"),
            dateTo: dayjs(endDate).format("LL"),
          })}
        </Text>
      </div>
      <Text fontStyle="BM">{word("box.programmation.activity.importTemplateForm.explanation")}</Text>

      <ErrorBoundary fallback={<ErrorView />}>
        <Suspense fallback={<Skeleton className="h-120 w-full" />}>
          <BoxProgrammationImportProgramSelection
            selectProgramId={selectProgramId}
            setSelectProgramId={setSelectProgramId}
          />
        </Suspense>
      </ErrorBoundary>

      <div className="flex flex-row items-center justify-between gap-6">
        <Button
          variant="outline"
          size="lg"
          className="flex-1"
          onClick={() => {
            hideSidebar()
          }}
        >
          {word("global.cancel")}
        </Button>

        <Button
          variant="primary"
          size="lg"
          className="flex-1"
          onClick={handleImportProgram}
          isLoading={isPending}
          disabled={!selectProgramId || isPending}
        >
          {isPending && <LoaderCircle className="animate-spin" />}
          {word("box.programmation.activity.importTemplateForm.import")}
        </Button>
      </div>
    </div>
  )
}
